import { createContext, useContext, useState, useCallback, type ReactNode } from 'react';
import type { DemoRole } from '@infoscend-claims/shared';
import { DEMO_USER_IDS } from '@infoscend-claims/shared';

export interface DemoPersona {
  role: DemoRole;
  userId: string;
  displayName: string;
  orgName: string;
}

interface DemoModeContextValue {
  persona: DemoPersona;
  setPersona: (p: DemoPersona) => void;
}

const DemoModeContext = createContext<DemoModeContextValue | null>(null);

const DEFAULT_PERSONA: DemoPersona = {
  role: 'brand_approver',
  userId: DEMO_USER_IDS.BRAND_APPROVER,
  displayName: 'Budi Santoso',
  orgName: 'Mondelez Indonesia',
};

function loadStoredPersona(): DemoPersona {
  try {
    const raw = localStorage.getItem('demoPersona');
    if (raw) {
      const parsed = JSON.parse(raw) as DemoPersona;
      if (parsed.role && parsed.userId) return parsed;
    }
  } catch {
    // ignore
  }
  return DEFAULT_PERSONA;
}

export function DemoModeProvider({ children }: { children: ReactNode }) {
  const [persona, setPersonaState] = useState<DemoPersona>(loadStoredPersona);

  const setPersona = useCallback((p: DemoPersona) => {
    localStorage.setItem('demoPersona', JSON.stringify(p));
    // Keep legacy demoRole key for any code that reads it directly
    localStorage.setItem('demoRole', p.role);
    setPersonaState(p);
  }, []);

  return (
    <DemoModeContext.Provider value={{ persona, setPersona }}>
      {children}
    </DemoModeContext.Provider>
  );
}

export function useDemoMode(): DemoModeContextValue {
  const ctx = useContext(DemoModeContext);
  if (!ctx) throw new Error('useDemoMode must be used within DemoModeProvider');
  return ctx;
}
